import * as React from 'react';
import { connect } from 'react-redux';
import { Stack, Spinner, SpinnerSize, MessageBar, MessageBarType, IconButton, SearchBox, PersonaSize, PersonaPresence, PersonaCoin, Toggle, TooltipHost } from 'office-ui-fabric-react';
import { AuditLogQuery, AuditLogItem } from '../../store/services/auditLogService';
import * as AuditLogsStore from '../../store/AuditLogsStore';
import { FilterAttribute } from '../common/FilterAttributes/FilterAttribute';
import { AuditLogsFields, createSubentityFieldForEntity } from './AuditLogsFields';
import './AuditLogs.css';
import { PersonInfo } from '../common/inputs/PersonPickerInput';
import { FormatDateTime, debounce } from '../utils/common';
import { RouteComponentProps } from 'react-router';
import { FieldChanges } from './FieldChanges';
import Collapsible from '../common/Collapsible';
import { groupAuditLogsByTimePeriod, getFirstGroupKey } from './auditLogGrouping';
import { withEntityResolution, WithEntityResolution } from '../common/EntityResolver';
import { canHaveSubentities, getLabelForEntityType } from './entitySubentityMapping';
import * as analytics from '../../analytics';
import { ApplicationState } from '../../store';
import { EntityType } from '../../entities/common';
import { ShowTimeProvider } from '../common/ShowTimeContext';

type StateProps = {
    items: AuditLogItem[];
    loading: boolean;
    canLoadMore: boolean;
    query: AuditLogQuery;
    error?: string;
    user: { id: string | null; name: string };
} & WithEntityResolution;

type DispatchProps = typeof AuditLogsStore.actionCreators;

type Props = StateProps & DispatchProps & RouteComponentProps<{ entityType?: string; entityId?: string }>;

function AuditLogsListImpl({
    items, loading, canLoadMore, query, error, user, load, loadMore, clear, entity, match, loadEntity
}: Props) {
    const [selectedUsers, setSelectedUsers] = React.useState<PersonInfo[]>();
    const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>({});
    const [bottomElement, setBottomElement] = React.useState<HTMLDivElement | null>(null);
    const [auditLogsContainer, setAuditLogsContainer] = React.useState<HTMLDivElement | null>(null);

    const fromDate = query.from ? new Date(query.from) : undefined;
    const toDate = query.to ? new Date(query.to) : undefined;

    // Extract URL parameters  
    const { entityType, entityId } = match.params;
    const isEntityFiltered = !!entityId;
    const isEntityTypeFiltered = !!entityType;

    // Determine if we should show subentity filter based on URL params or selected parent entities
    const effectiveParentEntities = entityType 
        ? entityType === EntityType.ArchivedProject 
            ? [EntityType.Project, EntityType.ArchivedProject] 
            : [entityType] 
        : (query.entityTypes ?? []);
    const showSubentityFilter = effectiveParentEntities.length > 0;

    const canLoadMoreItems = canLoadMore && items.length > 0;

    // Debounced search handler
    const debouncedSearch = React.useMemo(() => debounce<string | undefined>(1000), []);

    // Enhanced entity loading using EntityResolver 
    React.useEffect(() => {
        if (entityId && !entity) {
            loadEntity();
        }
    }, [entityId, entity, loadEntity]);

    // Initialize entity filtering from URL parameters on mount
    React.useEffect(() => {
        const initialQuery: Partial<AuditLogQuery> = {
            entityIds: isEntityFiltered ? [entityId!] : undefined,
            entityTypes: isEntityTypeFiltered ? effectiveParentEntities : undefined
        };
        load(initialQuery);

        // Track audit log page usage
        analytics.trackEvent("Audit Log: Page View", user, {
            viewType: isEntityFiltered ? 'entity' : (isEntityTypeFiltered ? 'entityType' : 'global'),
            entityType: entityType,
            hasEntityFilter: isEntityFiltered
        });

        return () => {
            clear();
        };
    }, [load, isEntityFiltered, isEntityTypeFiltered, entityId, entityType]);

    // Helper function to handle filter changes
    const handleFilterChange = (update: Partial<AuditLogQuery>) => {
        load(update);

        // Scroll to top of audit logs container after filter change
        if (auditLogsContainer) {
            auditLogsContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Helper function to process array values for filters
    const processArrayValue = (value: any): string[] | undefined => {
        if (value === undefined || value === null) {
            return undefined;
        }

        let result: string[] = [];
        if (Array.isArray(value)) {
            result = value.filter(v => !!v) as string[];
        } else if (value) {
            result = [value as string];
        }

        return result.length === 0 ? undefined : result;
    };

    // Group items by time periods for better visual organization
    const groupedItems = React.useMemo(() => {
        const grouped = groupAuditLogsByTimePeriod(items);

        if (grouped.length > 0) {
            const currentGroupKeys = grouped.map(g => g.groupKey);

            // Check if we have no expanded groups or none of the expanded groups exist in the current groups
            const hasValidExpandedGroups = Object.keys(expandedGroups).some(key =>
                expandedGroups[key] && currentGroupKeys.includes(key as any)
            );

            if (!hasValidExpandedGroups) {
                const firstGroupKey = getFirstGroupKey(grouped);
                if (firstGroupKey) {
                    setExpandedGroups({ [firstGroupKey]: true });
                }
            }
        }

        return grouped;
    }, [items]);

    // Infinite scroll observer
    React.useEffect(() => {
        if (!bottomElement) {
            return () => { };
        }

        const observer = new IntersectionObserver((entries) => {
            const first = entries[0];
            if (first.isIntersecting && canLoadMore && !loading) {
                loadMore();
            }
        }, { root: null, threshold: 0.1 });

        observer.observe(bottomElement);
        return () => observer.disconnect();
    }, [bottomElement, canLoadMore, loading, loadMore, expandedGroups]);

    return (
        <ShowTimeProvider value={true}>
        <Stack tokens={{ childrenGap: 0, padding: 0 }}>
            {error && <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>{error}</MessageBar>}

            {/* Header */}
            <Stack className="header-container">
                <Stack horizontal verticalAlign="center" className="header-container-inner">
                    <IconButton
                        iconProps={{ iconName: 'Back' }}
                        title="Back"
                        ariaLabel="Back"
                        className="header-back-button"
                        onClick={() => window.history.back()}
                    />
                    <span className="header-title">
                        {isEntityFiltered
                            ? `ACTIVITY LOG FOR ${getLabelForEntityType(entityType!)}: ${entity?.attributes?.Name || ""}`
                            : isEntityTypeFiltered
                                ? `ACTIVITY LOG FOR ${entityType === EntityType.User ? 'PEOPLE MANAGEMENT' : (getLabelForEntityType(entityType!) + "S")}`
                                : 'GLOBAL ACTIVITY LOG'
                        }
                    </span>
                </Stack>
            </Stack>

            {/* Filters */}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className="filters-container">
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                    <SearchBox
                        placeholder="Search"
                        className="search-box"
                        value={query.search ?? ''}
                        onChange={(_, v) => {
                            const search = v === '' ? undefined : v;
                            // Debounce search to avoid too many API calls while typing
                            debouncedSearch(search, (searchValue) => {
                                handleFilterChange({ search: searchValue });
                            });
                        }}
                    />
                    <TooltipHost
                        content={
                            query.searchMode === 'exact'
                                ? 'Fast mode: Text index with stemming. Matches complete words and their stems (e.g., "run" matches "running"). Example: "Iteration 1 Q1 2025" found by "Iteration" or "Q1". Not found by "Iter". For partial word matching, use Slow mode.'
                                : 'Slow mode: Matches any part of text. Example: "Iteration 1 Q1 2025" found by any part like "Iter", "tion 1", "Q1 202", etc.'
                        }
                    >
                        <Toggle
                            onText="Fast"
                            offText="Slow"
                            checked={query.searchMode === 'exact'}
                            onChange={(_, checked) => {
                                handleFilterChange({ searchMode: checked ? 'exact' : 'partial' });
                            }}
                            styles={{ root: { marginTop: 6 } }}
                        />
                    </TooltipHost>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 8 }} className="filter-attributes-container align-center">
                    <FilterAttribute
                        field={AuditLogsFields.date}
                        value={fromDate || toDate ? { from: fromDate?.toDateOnlyString(), to: toDate?.toDateOnlyString() } : undefined}
                        onEditComplete={(value) => {
                            const dateRange = value as { from?: string; to?: string } | null | undefined;
                            const newFrom = dateRange?.from;
                            const newTo = dateRange?.to;

                            const updatedFromDate = newFrom ? new Date(newFrom) : undefined;
                            const updatedToDate = newTo ? new Date(newTo) : undefined;

                            // Validate dates to avoid Invalid Date objects
                            const validFromDate = updatedFromDate && !isNaN(updatedFromDate.getTime()) ? updatedFromDate : undefined;
                            const validToDate = updatedToDate && !isNaN(updatedToDate.getTime()) ? updatedToDate : undefined;

                            handleFilterChange({
                                from: validFromDate?.getBeginOfDay().toISOString(),
                                to: validToDate?.getEndOfDay().toISOString()
                            });
                        }}
                    />

                    {/* Entity Type filter - only shown when not filtering by specific entity */}
                    {!isEntityTypeFiltered && (
                        <FilterAttribute
                            field={AuditLogsFields.entityType}
                            value={query.entityTypes}
                            onEditComplete={(value) => {
                                const entityTypes = processArrayValue(value);
                                handleFilterChange({ entityTypes, subEntityTypes: undefined });
                            }}
                        />
                    )}

                    {/* Sub-Entity filter - shown when parent entity is selected or when filtering by entity type */}
                    <FilterAttribute
                        field={createSubentityFieldForEntity(effectiveParentEntities)}
                        value={query.subEntityTypes}
                        disabled={!showSubentityFilter}
                        onEditComplete={(value) => {
                            const subEntityTypes = processArrayValue(value);
                            handleFilterChange({ subEntityTypes });
                        }}
                    />

                    <FilterAttribute
                        field={AuditLogsFields.eventType}
                        value={query.eventTypes}
                        onEditComplete={(value) => {
                            const eventTypes = processArrayValue(value);
                            handleFilterChange({ eventTypes });
                        }}
                    />

                    <FilterAttribute
                        field={AuditLogsFields.user}
                        value={selectedUsers}
                        onEditComplete={(value) => {
                            if (value === undefined || value === null) {
                                setSelectedUsers(undefined);
                                handleFilterChange({ userIds: undefined });
                                return;
                            }

                            // Convert to array of PersonInfo objects
                            let users: PersonInfo[] = [];
                            if (Array.isArray(value)) {
                                users = value as PersonInfo[];
                            } else {
                                users = [value as PersonInfo];
                            }

                            // Update selected users state
                            setSelectedUsers(users);

                            // Extract user IDs for the query
                            const userIds = users.map(u => u.id).filter(id => !!id);
                            const finalUserIds = userIds.length === 0 ? undefined : userIds;

                            handleFilterChange({ userIds: finalUserIds });
                        }}
                    />

                    <IconButton
                        iconProps={{ iconName: "ClearFilter" }}
                        title="Clear Filters"
                        ariaLabel="Clear Filters"
                        onClick={() => {
                            // Clear all local filter states
                            setSelectedUsers(undefined);

                            // Clear filters but preserve entity/entityType filtering based on mode
                            const baseQuery: Partial<AuditLogQuery> = {
                                subEntityTypes: undefined,
                                subEntityIds: undefined,
                                eventTypes: undefined,
                                userIds: undefined,
                                from: undefined,
                                to: undefined,
                            };
                            if(!isEntityTypeFiltered){
                                baseQuery.entityTypes = undefined;
                            }

                            // Use handleFilterChange to ensure consistent behavior with other filters
                            handleFilterChange(baseQuery);
                        }}
                        disabled={
                            !query.from &&
                            !query.to &&
                            (isEntityTypeFiltered || !query.entityTypes?.length) &&
                            !query.subEntityTypes?.length &&
                            !query.eventTypes?.length &&
                            !query.userIds?.length
                        }
                    />

                </Stack>
            </Stack>

            <div className="audit-logs-container" ref={setAuditLogsContainer}>
                {groupedItems.length === 0 && !loading && (
                    <div className="no-records-message">No audit log records found</div>
                )}
                {groupedItems.length === 0 && loading && (
                    <Spinner size={SpinnerSize.medium} className="spinner-container" label={`Loading...`} />
                )}

                {groupedItems.map(({ groupKey, items: groupItems }, index) => {
                    const isLastGroup = index === groupedItems.length - 1;

                    return (
                        <div key={groupKey} className="activity-date-group">
                            <Collapsible
                                title={groupKey}
                                expanded={expandedGroups[groupKey] || false}
                                onToggle={(expanded) => {
                                    setExpandedGroups(prev => ({
                                        ...prev,
                                        [groupKey]: expanded
                                    }));
                                }}
                                classNames={{
                                    head: "activity-collapsible-header",
                                    body: "activity-collapsible-body"
                                }}
                            >
                                {groupItems.map((it: any) => {
                                    const id = it.id;
                                    const ts = it.timeStamp || '';
                                    // Use subEntityType if available (for subentities), otherwise use entityType (for main entities)
                                    const displayEntityType = it.subEntityType || it.entityType;
                                    const entityName = it.entityName ?? it.subEntityId ?? it.entityId;
                                    const eventType = (it.eventType || '').toLowerCase();
                                    const userName = it.initiatorFullName || '';
                                    const userId = it.initiatorUserId || '';

                                    // Handle attribute deltas
                                    const attributeDeltas = it.changes || {};
                                    return (
                                        <div key={id} className="audit-log-item">
                                            {/* Date section */}
                                            <div className="audit-log-header">
                                                <div className="timestamp-container">
                                                    <div className="timestamp">
                                                        {FormatDateTime(ts, true)}
                                                    </div>
                                                </div>

                                                <div className="audit-log-content">
                                                    {/* User and action */}
                                                    <div className="user-action-container">
                                                        <div className="user-action-text">
                                                            <PersonaCoin
                                                                size={PersonaSize.size24}
                                                                presence={PersonaPresence.none}
                                                                text={userName || userId}
                                                            />
                                                            <span className="user-name">{userName || userId || "PPM Express"}</span>
                                                            <span>{eventType}</span>
                                                            <span>{getLabelForEntityType(displayEntityType || 'entity')}</span>
                                                            <span className="entity-name">{entityName}</span>
                                                        </div>
                                                    </div>

                                                    {/* Changes */}
                                                    <FieldChanges
                                                        attributeDeltas={attributeDeltas}
                                                        entityType={displayEntityType || it.entityType}
                                                        description={it.description}
                                                        message={it.message}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {/* Place infinite scroll sentinel in the last group */}
                                {isLastGroup && (<div ref={(el) => setBottomElement(el)}>
                                    {loading && <Spinner size={SpinnerSize.medium} className="spinner-container" label="Loading more..." />}
                                    {!loading && !canLoadMoreItems && <div className="no-records-message">
                                        {`${items.length} audit records shown.`}
                                    </div>}
                                </div>)}
                            </Collapsible>
                        </div>
                    );
                })}
            </div>
        </Stack>
        </ShowTimeProvider>
    );
}

const { mapStateToProps, mapDispatchToProps } = withEntityResolution<StateProps>(
    (state: ApplicationState) => ({
        items: state.auditLogs.items,
        loading: state.auditLogs.isLoading,
        canLoadMore: state.auditLogs.canLoadMore,
        query: state.auditLogs.query,
        error: state.auditLogs.error,
        user: { id: state.user.id, name: state.user.name },
    }),
    AuditLogsStore.actionCreators
);

export default connect(mapStateToProps, mapDispatchToProps)(AuditLogsListImpl);
